import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { isString, toLower, trim } from 'lodash';
import sleep from '../../utils/sleep';
import { selectAllObjects } from './data/objectsSlice';

const initialState = {
	searchString: '',
	searchResults: [],
	searchStatus: 'idle',
};

const searchInObjects = async (objects = [], searchString = '') => {
	await sleep(0);
	if (isString(searchString)) {
		const trimmedSearchString = trim(toLower(searchString));
		if (trimmedSearchString === '') {
			return [];
		}

		const searchWords = trimmedSearchString.split(' ');

		const searchResults = Object.values(objects).filter((object) => {
			if (object.id && toLower(object.id).includes('tutorial')) {
				return false;
			}

			return searchWords.every((searchWord) => toLower(object.title1).includes(searchWord)
				|| toLower(object.title2).includes(searchWord)
				|| toLower(object.subtitle).includes(searchWord)
				|| toLower(object.subtitle).includes(searchWord)
				|| toLower(object.name_file).includes(searchWord)
				|| toLower(object.keywords).includes(searchWord));
		});

		return searchResults
			.sort((a, b) => new Date(b.updated_at ?? 0) - new Date(a.updated_at ?? 0))
			.slice(0, 100);

		// TODO: Tracking for searchwords longer than 2 chars
	}

	return [];
};

export const search = createAsyncThunk('search/search', async (payload, { getState, dispatch }) => {
	const state = getState();

	const searchString = trim(toLower(payload?.searchString ?? ''));
	dispatch(setSearchString({ searchString }));

	const allObjects = selectAllObjects(state);
	if (searchString === '') {
		return { searchResults: []};
	}

	const searchResults = await searchInObjects(allObjects, searchString);

	return { searchResults };
});

const searchSlice = createSlice({
	name: 'search',
	initialState,
	reducers: {
		setSearchString: (state, action) => {
			if (action?.payload?.searchString !== null) {
				state.searchString = action.payload.searchString;
			}
		},
		setSearchResults: (state, action) => {
			if (action?.payload?.searchResults) {
				state.searchResults = action.payload.searchResults;
			}
		},
		resetSearch: (state) => {
			state.searchResults = initialState.searchResults;
			state.searchString = initialState.searchString;
			state.searchStatus = initialState.searchStatus;
		},
	},
	extraReducers: {
		[search.fulfilled]: (state, action) => {
			state.searchStatus = 'idle';
			state.searchResults = action.payload.searchResults;
		},
		[search.pending]: (state) => {
			state.searchStatus = 'pending';
		},
		[search.rejected]: (state) => {
			state.searchStatus = 'error';
		},
	},
});

export default searchSlice.reducer;
export const { setSearchString, setSearchResults, resetSearch } = searchSlice.actions;

export const selectSearchString = (state) => state.search.searchString;
export const selectSearchResults = (state) => state.search.searchResults;
export const selectSearchStatus = (state) => state.search.searchStatus;

export const selectSearchResultsCount = createSelector(
	selectSearchResults,
	(searchResults) => searchResults.length,
);
